import React, { useState, useEffect } from "react";
import {
	Box,
	Typography,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CustomAutocomplete from "../../../components/CustomAutocomplete";
import { UnidadeMedidaService } from "../../../service/unidade_medida_service";
import { CultivoService } from "../../../service/cultivo_service";

function AbaProdutos({ produtos, setProdutos, onAddProdutoClick }) {
	const [openDialog, setOpenDialog] = useState(false);
	const [editandoIndex, setEditandoIndex] = useState(null);
	const [cultivoSelecionado, setCultivoSelecionado] = useState(null);
	const [unidadeMedidaSelecionada, setUnidadeMedidaSelecionada] = useState(null);
	const [codigoBarras, setCodigoBarras] = useState("");
	const [cultivos, setCultivos] = useState([]);
	const [unidadesMedida, setUnidadesMedida] = useState([]);

	useEffect(() => {
		if (onAddProdutoClick) {
			onAddProdutoClick(() => handleAddProduto);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onAddProdutoClick]);

	useEffect(() => {
		loadCultivos();
		loadUnidadesMedida();
	}, []);

	const loadCultivos = async () => {
		try {
			const cultivoService = new CultivoService();
			const cultivationParams = {
				page: 1,
				limit: 100000,
				sortBy: JSON.stringify([
					{ cultura: "asc" },
					{ variedade: "asc" }
				])
			};
			const response = await cultivoService.getAll(cultivationParams);
			const cultivosArray = response?.cultivations || [];
			setCultivos(cultivosArray);
		} catch (error) {
			console.error("Erro ao carregar cultivos:", error);
			setCultivos([]);
		}
	};

	const loadUnidadesMedida = async () => {
		try {
			const unidadeMedidaService = new UnidadeMedidaService();
			const data = await unidadeMedidaService.getByCultivo();
			setUnidadesMedida(data);
		} catch (error) {
			console.error("Erro ao carregar unidades de medida:", error);
			setUnidadesMedida([]);
		}
	};

	const handleAddProduto = () => {
		setEditandoIndex(null);
		setCultivoSelecionado(null);
		setUnidadeMedidaSelecionada(null);
		setCodigoBarras("");
		setOpenDialog(true);
	};

	const handleEditProduto = (index) => {
		const produto = produtos[index];
		setEditandoIndex(index);
		setCultivoSelecionado(produto.cultivo || null);
		setUnidadeMedidaSelecionada(produto.unidadeMedida || null);
		setCodigoBarras(produto.codigoBarras || "");
		setOpenDialog(true);
	};

	const handleRemoveProduto = (index) => {
		const novosProdutos = produtos.filter((_, i) => i !== index);
		setProdutos(novosProdutos);
	};

	const handleSaveProduto = () => {
		if (!cultivoSelecionado) {
			alert("Selecione um cultivo");
			return;
		}

		if (!unidadeMedidaSelecionada) {
			alert("Selecione uma unidade de medida");
			return;
		}

		const novoProduto = {
			id: editandoIndex !== null ? produtos[editandoIndex].id : Date.now(),
			cultivo: cultivoSelecionado,
			unidadeMedida: unidadeMedidaSelecionada,
			codigoBarras: codigoBarras,
			cultivoId: cultivoSelecionado.id,
			unidadeMedidaId: unidadeMedidaSelecionada.id,
		};

		let novosProdutos;
		if (editandoIndex !== null) {
			novosProdutos = produtos.map((p, i) => (i === editandoIndex ? novoProduto : p));
		} else {
			novosProdutos = [...produtos, novoProduto];
		}

		setProdutos(novosProdutos);
		handleCloseDialog();
	};

	const handleCloseDialog = () => {
		setOpenDialog(false);
		setEditandoIndex(null);
		setCultivoSelecionado(null);
		setUnidadeMedidaSelecionada(null);
		setCodigoBarras("");
	};

	const getCultivoLabel = (cultivo) => {
		if (!cultivo) return "";
		if (cultivo.variedade) {
			return `${cultivo.cultura} - ${cultivo.variedade}`;
		}
		return cultivo.cultura;
	};

	return (
		<Box>
			<Typography
				variant="subtitle1"
				sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "16px", mb: 2 }}
			>
				Produtos do Cliente
			</Typography>

			{produtos.length > 0 ? (
				<TableContainer sx={{ px: 0 }}>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", pl: 0 }}>
									Cultivo
								</TableCell>
								<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", width: "180px" }}>
									Unidade de Comercialização
								</TableCell>
								<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", width: "200px" }}>
									Código de Barras
								</TableCell>
								<TableCell sx={{ color: "#1a9e8f", fontWeight: 600, fontSize: "15px", borderBottom: "1px solid #e0e0e0", pr: 0, width: "120px" }} align="center">
									Ações
								</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{produtos.map((produto, index) => (
								<TableRow key={produto.id} sx={{ "&:hover": { bgcolor: "#f9f9f9" } }}>
									<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0", pl: 0 }}>
										{getCultivoLabel(produto.cultivo)}
									</TableCell>
									<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0" }}>
										{produto.unidadeMedida?.sigla || "-"}
									</TableCell>
									<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0" }}>
										{produto.codigoBarras || "-"}
									</TableCell>
									<TableCell sx={{ fontSize: "15px", borderBottom: "1px solid #f0f0f0", pr: 0 }} align="center">
										<IconButton
											size="small"
											sx={{ color: "#1a9e8f" }}
											onClick={() => handleEditProduto(index)}
											title="Editar produto"
										>
											<EditIcon fontSize="small" />
										</IconButton>
										<IconButton
											size="small"
											onClick={() => handleRemoveProduto(index)}
											sx={{ color: "#ff4d4d", ml: 0.5 }}
											title="Excluir produto"
										>
											<DeleteIcon fontSize="small" />
										</IconButton>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			) : (
				<Box
					sx={{
						textAlign: "center",
						py: 4,
						px: 8,
						color: "#999",
						border: "2px dashed #e0e0e0",
						borderRadius: 1,
						width: "100%",
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						minHeight: "120px",
					}}
				>
					<Typography variant="body1" sx={{ fontSize: "16px", fontWeight: 500 }}>
						Nenhum produto adicionado
					</Typography>
				</Box>
			)}

			<Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
				<DialogTitle>
					{editandoIndex !== null ? "Editar Produto" : "Adicionar Produto"}
				</DialogTitle>
				<DialogContent>
					<Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
						<CustomAutocomplete
							options={cultivos}
							getOptionLabel={getCultivoLabel}
							getOptionId={(option) => option.id}
							value={cultivoSelecionado}
							onChange={(newValue) => setCultivoSelecionado(newValue)}
							label="Cultivo"
							required
							placeholder="Buscar cultivo..."
							noOptionsText="Nenhum cultivo encontrado"
						/>

						<CustomAutocomplete
							options={unidadesMedida}
							getOptionLabel={(option) => option.sigla || ""}
							getOptionId={(option) => option.id}
							value={unidadeMedidaSelecionada}
							onChange={(newValue) => setUnidadeMedidaSelecionada(newValue)}
							label="Unidade de Comercialização"
							required
							placeholder="Selecionar..."
						/>

						<TextField
							fullWidth
							label="Código de Barras"
							value={codigoBarras}
							onChange={(e) => setCodigoBarras(e.target.value)}
							InputLabelProps={{ style: { fontSize: "15px" } }}
							inputProps={{ style: { fontSize: "15px" } }}
						/>
					</Box>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2 }}>
					<Button onClick={handleCloseDialog} sx={{ color: "#666" }}>
						Cancelar
					</Button>
					<Button
						onClick={handleSaveProduto}
						variant="contained"
						sx={{
							bgcolor: "#1a9e8f",
							"&:hover": {
								bgcolor: "#138a7d",
							},
						}}
					>
						{editandoIndex !== null ? "Salvar" : "Adicionar"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export default AbaProdutos;
